import { useRef } from 'react'

import { Input } from '#/shared/ui/input'
import type { InputProps } from '#/shared/ui/input'

import { SlashCommandMenu } from './slash-command-menu'
import type { SlashCommandGroup, SlashCommandItem } from './slash-command'
import type { SlashKeyHandler } from './use-slash-command'
import { useInputSlash } from './use-input-slash'

const EMPTY_GROUPS: SlashCommandGroup[] = []

interface SlashTextInputProps extends Omit<InputProps, 'value' | 'onChange'> {
  value: string
  onChange: (value: string) => void
  /** Grouped fields for the `/` menu. Omit to disable it. */
  slashGroups?: SlashCommandGroup[]
  onSlashInsert?: (item: SlashCommandItem) => void
}

/** A single-line `<input>` with the Notion-style `/` quick-insert menu. The
 *  selected field's token text is spliced into the value. */
export function SlashTextInput({
  value,
  onChange,
  slashGroups,
  onSlashInsert,
  onKeyDown,
  onKeyUp,
  onClick,
  ...rest
}: SlashTextInputProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const keyRef = useRef<SlashKeyHandler>(() => false)
  const groups = slashGroups ?? EMPTY_GROUPS
  const slash = useInputSlash({
    inputRef,
    value,
    groups,
    onChange,
    onUse: onSlashInsert,
  })

  return (
    <>
      <Input
        ref={inputRef}
        value={value}
        onChange={(e) => {
          onChange(e.target.value)
          slash.syncCaret()
        }}
        onKeyDown={(e) => {
          if (slash.open && keyRef.current(e.nativeEvent)) {
            e.preventDefault()
            return
          }
          onKeyDown?.(e)
        }}
        onKeyUp={(e) => {
          slash.syncCaret()
          onKeyUp?.(e)
        }}
        onClick={(e) => {
          slash.syncCaret()
          onClick?.(e)
        }}
        {...rest}
      />
      {slash.open && (
        <SlashCommandMenu
          groups={groups}
          query={slash.query}
          rect={slash.rect}
          keyRef={keyRef}
          onSelect={slash.onSelect}
          onDismiss={slash.onDismiss}
        />
      )}
    </>
  )
}
